import { Trans } from "react-i18next";
import React, { Component, ErrorInfo, ReactNode } from "react";
import "@shared/styles/error-boundary.css";
import logger from "@shared/utils/logger";
import { getProductBrand } from "@shared/config/productBrand";

const PLATFORM_SUPPORT_EMAIL = getProductBrand().supportEmail;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  /** Org support_email — pass from context when available. Falls back to platform default. */
  supportEmail?: string | null;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    logger.error("ErrorBoundary caught an error:", error);

    // Update state with error details
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));

    // In production, you would send this to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-card card">
            <div className="card-body">
              <div className="error-icon-container">
                <i className="bi bi-exclamation-triangle-fill error-icon" aria-hidden="true" />
              </div>

              <h2 className="error-title"><Trans ns="features" i18nKey="ui.copy.8d886c0ba6f2" /></h2>

              <div className="error-alert alert alert-danger" role="alert">
                <h3 className="h5"><Trans ns="features" i18nKey="ui.copy.9ed76220ea43" /></h3>
                <p className="error-message">
                  {this.state.error?.message || "An unexpected error occurred"}
                </p>
              </div>

              {import.meta.env.DEV &&
                this.state.errorInfo && (
                  <details className="error-details">
                    <summary><Trans ns="features" i18nKey="ui.copy.cb4b0124fecd" /></summary>
                    <pre className="error-stack">
                      {this.state.error?.stack}
                      {"\n\n"}
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}

              <div className="error-actions">
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  onClick={this.handleReset}
                >
                  <i className="bi bi-arrow-clockwise me-2" aria-hidden="true" />
                  <Trans ns="features" i18nKey="ui.copy.cef2fe093b68" /></button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={this.handleReload}
                >
                  <Trans ns="features" i18nKey="ui.copy.6e41ee3f309a" /></button>
              </div>

              {this.state.errorCount > 2 && (
                <div className="alert alert-warning mt-3" role="alert">
                  <strong><Trans ns="features" i18nKey="ui.copy.a9b9fcc7d216" /></strong> <Trans ns="features" i18nKey="ui.copy.ceaede75f5f9" />{" "}
                  {this.state.errorCount} <Trans ns="features" i18nKey="ui.copy.11a572aecafb" />{" "}
                  <a
                    href={`mailto:${this.props.supportEmail || PLATFORM_SUPPORT_EMAIL}`}
                  >
                    <Trans ns="features" i18nKey="ui.copy.2b658ffd9420" /></a>{" "}
                  <Trans ns="features" i18nKey="ui.copy.70311279d8e1" /></div>
              )}

              <div className="error-help-text">
                <small className="text-muted">
                  <Trans ns="features" i18nKey="ui.copy.90250acb8164" />{" "}
                  <a
                    href={`mailto:${this.props.supportEmail || PLATFORM_SUPPORT_EMAIL}`}
                  >
                    <Trans ns="features" i18nKey="ui.copy.987e66e1b240" /></a>{" "}
                  <Trans ns="features" i18nKey="ui.copy.e7c14c40f051" /></small>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
) => {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

// Hook for error handling in functional components
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return setError;
};

export default ErrorBoundary;
